/**
 * Auto-height resize emitter for embeddable public surfaces (meetings-embed-launcher dispatch) —
 * the React-side mirror of the ordinance viewer's static-page resize script
 * (services/ordinance-ingest/pkg/generate/ordinance-ask.js's `postMessage({type:"towndesk:code-
 * resize", height})` IIFE). Only the "meetings" inline embed uses this today; factored as a
 * standalone hook (rather than inlined in MeetingsPage) so any other SPA route can adopt the same
 * auto-height behavior later without re-deriving the ResizeObserver plumbing.
 *
 * Posts `{type: messageType, height}` to `window.parent` whenever the observed element's rendered
 * height changes (and once on mount) — apps/widget's inline mounters listen for exactly this
 * message (origin-checked on the RECEIVING end, same posture as the code widget's
 * `towndesk:code-resize` receiver; "*" here is a harmless no-op when not actually framed, same
 * reasoning as ordinance-ask.js). `messageType` defaults to `"towndesk:meetings-resize"` (the
 * original + only caller at the time this hook was factored out); the FAQ inline embed passes
 * `"towndesk:faq-resize"` so its own `mountFaqInline` listener can distinguish the channel.
 *
 * No-op when `embed` is false (standalone page — nothing listening, and no reason to pay for a
 * ResizeObserver) and when `window.self === window.top` (not actually inside an iframe).
 */
import { useEffect } from "react";

export function useEmbedResize(
  embed: boolean,
  ref: React.RefObject<HTMLElement>,
  messageType = "towndesk:meetings-resize",
): void {
  useEffect(() => {
    if (!embed) return;
    if (window.self === window.top) return; // not embedded — nothing to report
    const el = ref.current;
    if (!el) return;

    // -1 (not 0) is the "never posted yet" sentinel — a genuinely-empty content root legitimately
    // measures 0px (e.g. a "No published meetings yet." one-liner in a test/jsdom environment with
    // no real layout engine), and that 0 must still be posted at least once so the launcher's
    // iframe isn't stuck at its static data-td-height fallback forever.
    let lastHeight = -1;
    const postHeight = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      if (h === lastHeight || h < 0) return;
      lastHeight = h;
      window.parent.postMessage({ type: messageType, height: h }, "*");
    };

    postHeight();

    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(postHeight);
      ro.observe(el);
      return () => ro.disconnect();
    }
    // Fallback for environments without ResizeObserver — same posture as ordinance-ask.js.
    window.addEventListener("resize", postHeight);
    const t = setInterval(postHeight, 500);
    return () => {
      window.removeEventListener("resize", postHeight);
      clearInterval(t);
    };
  }, [embed, ref, messageType]);
}
